"use client";

const STORAGE_KEY = "idx-launchlab-recent-launches";
const MAX_PER_WALLET = 24;

/**
 * @typedef {{
 *   mint: string;
 *   name: string;
 *   symbol: string;
 *   imgUrl?: string;
 *   launchedAt: string;
 * }} RecentLaunch
 */

/** @returns {Record<string, RecentLaunch[]>} */
function readAll() {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** @param {Record<string, RecentLaunch[]>} data */
function writeAll(data) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota errors */
  }
}

/**
 * @param {{ wallet: string; mint: string; name: string; symbol: string; imgUrl?: string }} launch
 */
export function recordRecentLaunch({ wallet, mint, name, symbol, imgUrl }) {
  const w = wallet?.trim();
  const m = mint?.trim();
  if (!w || !m) return;

  const all = readAll();
  const prev = Array.isArray(all[w]) ? all[w] : [];
  const next = [
    {
      mint: m,
      name: name?.trim() || "New coin",
      symbol: symbol?.trim() || m.slice(0, 4).toUpperCase(),
      imgUrl: imgUrl?.trim() || undefined,
      launchedAt: new Date().toISOString(),
    },
    ...prev.filter((row) => row.mint !== m),
  ].slice(0, MAX_PER_WALLET);

  all[w] = next;
  writeAll(all);
}

/** @param {string} wallet @returns {RecentLaunch[]} */
export function getRecentLaunches(wallet) {
  const w = wallet?.trim();
  if (!w) return [];
  const all = readAll();
  return Array.isArray(all[w]) ? all[w] : [];
}

/**
 * @param {import("@/lib/idx-launchlab/parse-launch-feed").LaunchlabFeedRow[]} apiItems
 * @param {RecentLaunch[]} recent
 * @returns {import("@/lib/idx-launchlab/parse-launch-feed").LaunchlabFeedRow[]}
 */
/**
 * Mint addresses for creator reward attribution — API coins plus browser-stored launches.
 * @param {Array<{ address?: string }>} myCoins
 * @param {RecentLaunch[]} [recent]
 * @returns {Set<string>}
 */
export function buildCreatorMintSet(myCoins, recent) {
  /** @type {Set<string>} */
  const mints = new Set();
  for (const coin of myCoins ?? []) {
    const mint = coin?.address?.trim();
    if (mint) mints.add(mint);
  }
  for (const row of recent ?? []) {
    const mint = row?.mint?.trim();
    if (mint) mints.add(mint);
  }
  return mints;
}

export function mergeRecentLaunches(apiItems, recent) {
  const byMint = new Map((apiItems ?? []).map((item) => [item.address, item]));
  /** @type {import("@/lib/idx-launchlab/parse-launch-feed").LaunchlabFeedRow[]} */
  const out = [...(apiItems ?? [])];

  for (const row of recent ?? []) {
    if (!row.mint || byMint.has(row.mint)) continue;
    out.unshift({
      address: row.mint,
      poolId: "",
      tokenName: row.name,
      tokenSymbol: row.symbol,
      createdAt: row.launchedAt,
      marketCapUsd: 0,
      liquidityUsd: 0,
      volume24hUsd: 0,
      progressPercent: 0,
      status: "funding",
      imgUrl: row.imgUrl,
      indexing: true,
    });
  }

  return out.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/**
 * Graduated coins inferred from Creator Fee Keys (Raydium launch index may lag or omit them).
 * @param {import("@/lib/idx-launchlab/parse-launch-feed").LaunchlabFeedRow[]} apiItems
 * @param {import("@/lib/idx-launchlab/fee-keys").LaunchlabFeeKeyRow[]} feeKeys
 * @param {Map<string, string>} [createdAtByMint]
 */
export function mergeFeeKeyLaunches(apiItems, feeKeys, createdAtByMint) {
  const byMint = new Map((apiItems ?? []).map((item) => [item.address, item]));
  /** @type {import("@/lib/idx-launchlab/parse-launch-feed").LaunchlabFeedRow[]} */
  const out = [...(apiItems ?? [])];

  for (const fk of feeKeys ?? []) {
    const mint = fk.coinMint?.trim();
    if (!mint || fk.feeKeyRole === "platform") continue;

    const existing = byMint.get(mint);
    if (existing) {
      const next = {
        ...existing,
        imgUrl: existing.imgUrl || fk.imgUrl,
        createdAt: existing.createdAt || createdAtByMint?.get(mint) || "",
        creatorAddress: existing.creatorAddress || fk.creatorAddress,
      };
      if (next !== existing) {
        byMint.set(mint, next);
        const idx = out.findIndex((item) => item.address === mint);
        if (idx >= 0) out[idx] = next;
      }
      continue;
    }
  }

  return out.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}
